import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Loader2, CheckCircle, XCircle, Music, Headphones, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const LOGO_URL = "/wordmark-tight.png";

export const InvitePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, token, axiosInstance, refreshUser } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, valid, invalid, applied, already_has_access
  const [codeInfo, setCodeInfo] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    checkInviteCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const checkInviteCode = async () => {
    if (!code) {
      setStatus('invalid');
      return;
    }

    // If user already has access, show that
    if (user?.has_access) {
      setStatus('already_has_access');
      return;
    }

    try {
      // Check if code is valid (we'll create this endpoint)
      const res = await axiosInstance.get(`/access/check/${code.toUpperCase()}`);
      if (res.data.valid) {
        setCodeInfo(res.data);
        setStatus('valid');
      } else {
        setStatus('invalid');
      }
    } catch (err) {
      setStatus('invalid');
    }
  };

  const applyCode = async () => {
    if (!token) {
      // Not logged in - redirect to login with the code
      navigate(`/login?invite=${code}`);
      return;
    }

    setApplying(true);
    try {
      const res = await axiosInstance.post('/access/validate', { code: code.toUpperCase() });
      if (res.data.success) {
        setCodeInfo({ ...codeInfo, role_type: res.data.role });
        setStatus('applied');
        toast.success(res.data.role === 'artist' ? 'Welcome, Artist!' : "You're in!");
        await refreshUser();
        setTimeout(() => navigate('/'), 2500);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply invite code');
      setStatus('invalid');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6">
      {/* Logo */}
      <img 
        src={LOGO_URL} 
        alt="AUDIVUE" 
        className="h-20 sm:h-28 object-contain mb-8"
      />

      {/* Loading State */}
      {status === 'loading' && (
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Checking invite code...</p>
        </div>
      )}

      {/* Invalid Code */}
      {status === 'invalid' && (
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Invite</h1>
          <p className="text-zinc-400 text-sm mb-6">
            This invite code is invalid, expired, or has reached its usage limit.
          </p>
          <Button 
            onClick={() => navigate('/login')}
            className="bg-white text-black hover:bg-zinc-200"
          >
            Go to Login
          </Button>
        </div>
      )}

      {/* Valid Code - Ready to Apply */}
      {status === 'valid' && (
        <div className="text-center max-w-sm">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
            codeInfo?.role_type === 'artist' ? 'bg-orange-500/20' : 'bg-emerald-500/20'
          }`}>
            {codeInfo?.role_type === 'artist' ? (
              <Music className="w-10 h-10 text-orange-500" />
            ) : (
              <Headphones className="w-10 h-10 text-emerald-500" />
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">You're Invited!</h1>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Invite Code</p>
            <p className="text-white font-mono text-xl font-bold">{code.toUpperCase()}</p>
            <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              codeInfo?.role_type === 'artist' 
                ? 'bg-orange-500/20 text-orange-400' 
                : 'bg-emerald-500/20 text-emerald-400'
            }`}>
              {codeInfo?.role_type === 'artist' ? (
                <>
                  <Music className="w-4 h-4" />
                  Artist Access
                </>
              ) : (
                <>
                  <Headphones className="w-4 h-4" />
                  Listener Access
                </>
              )}
            </div>
          </div>

          <p className="text-zinc-400 text-sm mb-6">
            {codeInfo?.role_type === 'artist' 
              ? 'This code grants you Artist access - upload videos and build your audience!'
              : 'This code grants you access to discover amazing AI music videos!'
            }
          </p>

          {token ? (
            <Button 
              onClick={applyCode}
              disabled={applying}
              className={`w-full ${
                codeInfo?.role_type === 'artist'
                  ? 'bg-orange-500 hover:bg-orange-600'
                  : 'bg-emerald-500 hover:bg-emerald-600'
              } text-white`}
              data-testid="apply-invite-btn"
            >
              {applying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accept Invite
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-3">
              <Button 
                onClick={() => navigate(`/login?invite=${code}`)}
                className="w-full bg-white text-black hover:bg-zinc-200"
              >
                Sign In to Accept
              </Button>
              <Button 
                onClick={() => navigate(`/register?invite=${code}`)}
                variant="outline"
                className="w-full border-zinc-700 text-white hover:bg-zinc-800"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create Account
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Already Has Access */}
      {status === 'already_has_access' && (
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-blue-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">You Already Have Access!</h1>
          <p className="text-zinc-400 text-sm mb-6">
            You're already a member of AUDIVUE. Enjoy the music!
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="bg-white text-black hover:bg-zinc-200"
          >
            Go to Home
          </Button>
        </div>
      )}

      {/* Successfully Applied */}
      {status === 'applied' && (
        <div className="text-center max-w-sm">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
            codeInfo?.role_type === 'artist' ? 'bg-orange-500/20' : 'bg-green-500/20'
          }`}>
            {codeInfo?.role_type === 'artist' ? (
              <Music className="w-10 h-10 text-orange-500" />
            ) : (
              <Headphones className="w-10 h-10 text-green-500" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {codeInfo?.role_type === 'artist' ? 'Welcome, Artist!' : "You're In!"}
          </h1>
          <p className="text-zinc-400 text-sm mb-4">
            {codeInfo?.role_type === 'artist' 
              ? 'You can now upload videos and earn from tips!' 
              : 'Enjoy discovering amazing AI music videos!'
            }
          </p>
          <p className="text-zinc-500 text-xs">Redirecting to home...</p>
        </div>
      )}
    </div>
  );
};
